import { useMemo } from 'react';
import { ImageCarousel } from '../files';
import { formatScanDate as defaultFormatScanDate, parseScanDate } from './formatScanDate';
import { scanTakenAt } from './scanTakenAt';
import { ScanTimeline } from './timeline';
import type { DmScanSummary } from './types';
import { useComparePair, type ComparePaneState } from './useComparePair';
import type { DmScansQueryConfig } from './useDmScansQueries';

export interface ScanComparePanelProps {
    queries: DmScansQueryConfig;
    patientId: string | null;
    scans: DmScanSummary[] | null | undefined;
    totalAligners?: number | null;
    formatScanDate?: (value: string) => string;
    /** Kept in step with the single-scan view; see `DmScanList`. */
    tracksAligners?: boolean;
}

// parseScanDate, not raw Date: bare YYYY-MM-DD parses as UTC here and local in the
// timeline, which would order the pane differently from the markers it feeds.
function byDateAsc(a: DmScanSummary, b: DmScanSummary): number {
    return (
        (parseScanDate(scanTakenAt(a))?.getTime() ?? 0) -
        (parseScanDate(scanTakenAt(b))?.getTime() ?? 0)
    );
}

interface ComparePaneProps {
    scans: DmScanSummary[];
    pane: ComparePaneState;
    totalAligners: number | null;
    formatScanDate: (value: string) => string;
    /** The A pane mirrors so both photos sit against the shared centre line. */
    pagerSide?: 'left' | 'right';
    tracksAligners: boolean;
}

// Mirrors the Photos view: the same timeline on top, then the same carousel, so a
// compare pane and the single-scan view read alike.
function ComparePane({
    scans,
    pane,
    totalAligners,
    formatScanDate,
    pagerSide = 'right',
    tracksAligners
}: ComparePaneProps) {
    const { images, labels, thumbnails } = pane.carousel;

    return (
        <div className="flex flex-col min-w-0 min-h-0 gap-2">
            <ScanTimeline
                scans={scans}
                selectedScanId={pane.scanId}
                selectedScan={pane.scan}
                onSelectScan={pane.onSelectScan}
                totalAligners={totalAligners}
                formatScanDate={formatScanDate}
                tracksAligners={tracksAligners}
            />

            <div className="flex-1 min-h-0">
                {pane.isLoading ? (
                    <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse" />
                ) : (
                    <ImageCarousel
                        images={images}
                        labels={labels}
                        thumbnails={thumbnails}
                        pagerSide={pagerSide}
                        resetKey={pane.scanId ?? undefined}
                        index={pane.photoIndex}
                        onIndexChange={pane.onSelectPhoto}
                    />
                )}
            </div>
        </div>
    );
}

function ScanComparePanel({
    queries,
    patientId,
    scans,
    totalAligners = null,
    formatScanDate = defaultFormatScanDate,
    tracksAligners = true
}: ScanComparePanelProps) {
    const ordered = useMemo(() => [...(scans ?? [])].sort(byDateAsc), [scans]);
    const panes = useComparePair(queries, patientId, ordered);

    if (ordered.length < 2) {
        return (
            <div className="flex-1 min-h-0 flex items-center justify-center text-sm text-gray-500 text-center px-4">
                At least two scans are needed to compare.
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-4">
            <ComparePane
                scans={ordered}
                pane={panes.left}
                totalAligners={totalAligners}
                formatScanDate={formatScanDate}
                tracksAligners={tracksAligners}
                pagerSide="left"
            />
            <ComparePane
                scans={ordered}
                pane={panes.right}
                totalAligners={totalAligners}
                formatScanDate={formatScanDate}
                tracksAligners={tracksAligners}
            />
        </div>
    );
}

export { ScanComparePanel };
